// User detail — composite 360° dashboard. Header carries the actions
// that used to live in the list (suspend / reactivate / revoke
// sessions), so the table can stay clickable rows-only. Cards present
// wallets, profile, authentication (2FA + sessions), the login log,
// positions/orders, and a recent audit timeline.
//
// SM-493: the money on this page used to be ONE wallet. The composite
// endpoint's `wallet` section is the GOLD one by construction —
// handleUserDetail passes an empty currency and ledger defaults that to
// GOLD (G3) — so a user's RED and GREEN holdings were invisible here
// and an operator had to already suspect a currency to go looking for
// it. Absence of a figure reads as zero, which is how a balance gets
// missed. Every currency the ledger knows about is now on the page,
// zero ones included, each with its own wallet identity and its own
// four balance slots. Nothing is summed across them (G8): a total that
// spans currencies is not a quantity of anything.
//
// The currency SET comes from ledger's /balances read and the currency
// LOOK comes from the operator's registry (name, symbol, artwork,
// precision) via CurrencyPresentationProvider — so enabling or
// re-skinning a currency shows up here without a console deploy.
//
// Every section is best-effort — an
// individual upstream failure costs its own section, not the page
// (mirrors handleUserDetail's contract in backoffice-service). Where
// that contract lets us tell "upstream failed" from "genuinely empty"
// — the section key is simply absent from the response — the two are
// rendered differently, because an operator asking whether a user has
// exposure must not read one as the other.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import {
  fetchUserDetail,
  fetchWalletBalances,
  setUserStatus,
  revokeUserSessions,
  keys,
  type WalletBalanceRow,
} from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { UserFeePanel } from "@/components/users/UserFeePanel";
import { UserLoginLogCard } from "@/components/users/UserLoginLogCard";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyHint } from "@/components/ui/EmptyHint";
import { ErrorHint } from "@/components/ui/ErrorHint";
import { Avatar } from "@/components/ui/Avatar";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { TechHint } from "@/components/ui/TechHint";
import { useMarketLabels, type MarketLabel } from "@/hooks/useCatalogNames";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";
import {
  fmtDate,
  fmtPrice,
  fmtQty,
  fmtRelative,
  shortId,
  statusLabel,
} from "@/lib/format";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import {
  resolveWalletRows,
  UserAvailableKpi,
  UserWalletsCard,
} from "@/components/users/UserWallets";
import type {
  IdentityItem,
  MFAFactorItem,
  OrderItem,
  PositionItem,
  SessionItem,
  SettlementTradeItem,
  UserAuthEvent,
  UserProfile,
} from "@/lib/types";

export const Route = createFileRoute("/_app/users/$id")({
  component: UserDetailPage,
});

type PendingAction = "suspend" | "reactivate" | "revoke" | null;

function UserDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const perms = useMyPerms();
  const detail = useQuery({
    queryKey: keys.userDetail(id),
    queryFn: () => fetchUserDetail(id),
  });
  // Every currency this user holds, in one read. Gated on WALLET_VIEW
  // because that is the permission the endpoint itself checks — asking
  // without it buys a 403 and nothing else, and this page has plenty to
  // show an operator who may only read profiles. Such an operator keeps
  // the single wallet the composite already carries (see walletRows).
  //
  // A READ, not an ensure: ledger does not materialise missing wallets
  // on this path, so opening a user never authors ledger state, and a
  // currency the user has never touched comes back as a true zero
  // rather than as a new row.
  const canReadWallets = perms.loaded && perms.has(Perm.WalletView);
  const balances = useQuery({
    queryKey: keys.walletBalances(id),
    queryFn: () => fetchWalletBalances(id),
    enabled: canReadWallets,
  });

  const [pending, setPending] = useState<PendingAction>(null);
  const statusMut = useMutation({
    mutationFn: ({ st, reason }: { st: string; reason: string }) =>
      setUserStatus(id, st, reason),
    onSuccess: () => {
      setPending(null);
      qc.invalidateQueries({ queryKey: keys.userDetail(id) });
      qc.invalidateQueries({ queryKey: keys.users });
    },
    meta: { successMessage: "User status updated." },
  });
  const revokeAll = useMutation({
    mutationFn: () => revokeUserSessions(id),
    onSuccess: () => {
      setPending(null);
      qc.invalidateQueries({ queryKey: keys.userDetail(id) });
    },
    meta: { successMessage: "All sessions revoked." },
  });
  const closeModal = () => {
    statusMut.reset();
    revokeAll.reset();
    setPending(null);
  };

  const profile = detail.data?.profile;
  const wallet = detail.data?.wallet;
  // The currency set, resolved once for both the summary and the card.
  //
  // Three states, and they are NOT the same fact:
  //   rows.length > 0 + complete   — the whole picture, zeros included.
  //   rows.length > 0 + !complete  — only the composite's GOLD wallet,
  //                                  because the all-currency read failed
  //                                  or this operator may not make it.
  //                                  Say so; a partial list that looks
  //                                  whole is how a balance goes missing.
  //   rows.length === 0            — no wallet on file at all.
  const { rows: walletRows, complete: walletsComplete } = useMemo(
    () => resolveWalletRows(balances.data, wallet),
    [balances.data, wallet],
  );
  const positions = detail.data?.positions?.positions ?? [];
  const orders = detail.data?.orders?.orders ?? [];
  const trades = detail.data?.trades?.trades ?? [];
  // handleUserDetail is best-effort per section: a section that isn't in
  // the response is one whose upstream errored, and a section that is
  // there with no rows is genuinely empty. Keeping the two apart is what
  // stops a dead settlement-service from reading as "0 positions" the
  // way it did for weeks in SM-190.
  const available = {
    positions: detail.data?.positions !== undefined,
    orders: detail.data?.orders !== undefined,
    trades: detail.data?.trades !== undefined,
  };
  const sessions = detail.data?.sessions?.sessions ?? [];
  const audit = detail.data?.audit?.events ?? [];
  const factors = profile?.mfa_factors ?? [];
  const activeFactors = factors.filter((f) => f.status === "ACTIVE");
  const activeSessions = sessions.filter((s) => !s.status || s.status === "ACTIVE");
  const status = profile?.status ?? "—";
  const isActive = status === "ACTIVE";

  return (
    <div className="space-y-6">
      <div>
        <Link to="/users" className="text-2xs text-muted hover:text-fg">
          ← back to users
        </Link>
      </div>

      {detail.isLoading && <DetailSkeleton />}

      {detail.isError && !detail.data && (
        <Card>
          <CardBody className="space-y-3 py-10 text-center">
            <div className="text-sm font-medium text-fg">Couldn't load this user</div>
            <p className="mx-auto max-w-md text-2xs text-muted">
              {(detail.error as Error)?.message ||
                "The backoffice service didn't answer. The user may still exist."}
            </p>
            <Button variant="ghost" onClick={() => void detail.refetch()}>
              Try again
            </Button>
          </CardBody>
        </Card>
      )}

      {detail.data && (
        <>
          <ProfileHeader
            id={id}
            profile={profile}
            isActive={isActive}
            onSuspend={() => setPending("suspend")}
            onReactivate={() => setPending("reactivate")}
            onRevoke={() => setPending("revoke")}
          />

          <KpiStrip
            rows={walletRows}
            walletsLoading={balances.isPending && canReadWallets}
            positionsOpen={available.positions ? positions.length : null}
            ordersOpen={available.orders ? orders.length : null}
            activeFactors={activeFactors.length}
            activeSessions={activeSessions.length}
          />

          {/* min-w-0 on both columns is load-bearing: a grid item sizes
              to its content by default, so the widest activity table
              would push the whole column past the viewport instead of
              scrolling inside its own card. */}
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="min-w-0 space-y-6 lg:col-span-2">
              <UserWalletsCard
                userID={id}
                rows={walletRows}
                complete={walletsComplete}
                loading={balances.isPending && canReadWallets}
                error={balances.isError ? (balances.error as Error) : null}
                onRetry={() => void balances.refetch()}
                permitted={canReadWallets}
              />
              <UserFeePanel userID={id} />
              <AuthenticationCard
                factors={factors}
                sessions={sessions}
                identities={profile?.identities ?? []}
              />
              {/* Its own read and pager (SM-821) — keyed by user so the
                  page index never carries over to the next user. */}
              <UserLoginLogCard key={id} userID={id} />
              <ActivityCard
                positions={positions}
                orders={orders}
                trades={trades}
                available={available}
                refreshing={detail.isFetching}
              />
            </div>
            <div className="min-w-0 space-y-6">
              <ProfileCard profile={profile} />
              <AuditCard events={audit} />
            </div>
          </div>
        </>
      )}

      <ConfirmModal
        open={pending === "suspend"}
        tone="danger"
        title="Suspend this user?"
        description={
          <>
            <p>
              <span className="font-mono text-2xs text-t-2">{profile?.email}</span> will be blocked from login,
              deposits, withdrawals, and order placement. Existing positions stay open until resolved.
            </p>
            <p className="mt-2 text-2xs">Suspension is reversible from this same screen.</p>
          </>
        }
        confirmLabel="Suspend account"
        cancelLabel="Keep active"
        reasonLabel="Reason (audit log)"
        reasonRequired
        loading={statusMut.isPending}
        error={statusMut.isError ? (statusMut.error as Error).message : null}
        onConfirm={(r) => statusMut.mutate({ st: "SUSPENDED", reason: r })}
        onCancel={closeModal}
      />

      <ConfirmModal
        open={pending === "reactivate"}
        tone="primary"
        title="Reactivate this user?"
        description={
          <p>
            Restores login, deposits, withdrawals, and order placement for{" "}
            <span className="font-mono text-2xs text-t-2">{profile?.email}</span>.
          </p>
        }
        confirmLabel="Reactivate"
        cancelLabel="Keep suspended"
        reasonLabel="Reason (audit log)"
        loading={statusMut.isPending}
        error={statusMut.isError ? (statusMut.error as Error).message : null}
        onConfirm={(r) => statusMut.mutate({ st: "ACTIVE", reason: r })}
        onCancel={closeModal}
      />

      <ConfirmModal
        open={pending === "revoke"}
        tone="danger"
        title="Revoke all sessions?"
        description={
          <>
            <p>
              Force-logs out every active session for{" "}
              <span className="font-mono text-2xs text-t-2">{profile?.email}</span> across all devices. The
              user can sign back in immediately.
            </p>
            <p className="mt-2 text-2xs">Use when credentials may be compromised, or after a password reset.</p>
          </>
        }
        confirmLabel="Revoke sessions"
        cancelLabel="Cancel"
        loading={revokeAll.isPending}
        error={revokeAll.isError ? (revokeAll.error as Error).message : null}
        onConfirm={() => revokeAll.mutate()}
        onCancel={closeModal}
      />
    </div>
  );
}

// --- header ------------------------------------------------------------

function ProfileHeader({
  id,
  profile,
  isActive,
  onSuspend,
  onReactivate,
  onRevoke,
}: {
  id: string;
  profile?: UserProfile;
  isActive: boolean;
  onSuspend: () => void;
  onReactivate: () => void;
  onRevoke: () => void;
}) {
  const display = profile?.display_name || profile?.email || id;
  return (
    <div className="card relative overflow-hidden">
      {/* soft accent wash so the user-header reads as the page's hero */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(800px 220px at 0% 0%, rgba(184, 91, 255, 0.10), transparent 60%)",
        }}
      />
      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <Avatar url={profile?.avatar_url} name={display} size={64} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-2xl tracking-tight text-fg">{display}</h1>
              <Badge tone={statusTone(profile?.status)}>{statusLabel(profile?.status)}</Badge>
              {profile?.kyc_level && profile.kyc_level !== "NONE" && (
                <Badge tone="accent">KYC · {profile.kyc_level}</Badge>
              )}
            </div>
            <div className="mt-1 text-xs text-muted">{profile?.email ?? "—"}</div>
            <div className="mt-0.5 font-mono text-2xs text-muted">{id}</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" onClick={onRevoke} className="text-danger">
            Revoke sessions
          </Button>
          {isActive ? (
            <Button variant="danger" onClick={onSuspend}>
              Suspend account
            </Button>
          ) : (
            <Button variant="primary" onClick={onReactivate}>
              Reactivate
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function statusTone(s: string | undefined): "success" | "warning" | "danger" | "muted" {
  switch (s) {
    case "ACTIVE":
      return "success";
    case "SUSPENDED":
      return "warning";
    case "CLOSED":
      return "danger";
    default:
      return "muted";
  }
}

// --- kpi strip ---------------------------------------------------------

function KpiStrip({
  rows,
  walletsLoading,
  positionsOpen,
  ordersOpen,
  activeFactors,
  activeSessions,
}: {
  rows: WalletBalanceRow[];
  walletsLoading: boolean;
  /** null = the section's upstream failed, so no count is known. */
  positionsOpen: number | null;
  ordersOpen: number | null;
  activeFactors: number;
  activeSessions: number;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      {/* The summary used to be two GOLD tiles. It is one tile per
          currency now, and it stays a LIST rather than becoming a
          headline figure: there is no single number here, because
          adding RED to GOLD produces a quantity of nothing (G8). */}
      <UserAvailableKpi rows={rows} loading={walletsLoading} />
      {/* An unavailable section shows "—", never 0: a zero here is a
          statement about the user's book, and printing it when the
          upstream never answered is the exact lie SM-190 shipped. */}
      <Kpi
        label="Open positions"
        value={positionsOpen === null ? "—" : positionsOpen.toLocaleString()}
        sub={positionsOpen === null ? "unavailable" : "active settlements"}
      />
      <Kpi
        label="Open orders"
        value={ordersOpen === null ? "—" : ordersOpen.toLocaleString()}
        sub={ordersOpen === null ? "unavailable" : "resting in book"}
      />
      <Kpi
        label="Auth factors"
        value={`${activeFactors}`}
        sub={`${activeSessions} active session${activeSessions === 1 ? "" : "s"}`}
        tone={activeFactors > 0 ? "success" : "danger"}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
  valueSize = "text-2xl",
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  tone?: "success" | "warning" | "danger";
  valueSize?: string;
}) {
  const valueClass =
    tone === "success"
      ? "text-yes"
      : tone === "warning"
        ? "text-warn"
        : tone === "danger"
          ? "text-no"
          : "text-fg";
  return (
    <div className="card min-w-0">
      <div className="text-2xs font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className={`num mt-1.5 ${valueSize} font-bold tracking-tight ${valueClass}`}>{value}</div>
      {sub && <div className="mt-1 text-2xs text-muted">{sub}</div>}
    </div>
  );
}

// --- authentication (2FA + sessions + identities) ---------------------

function AuthenticationCard({
  factors,
  sessions,
  identities,
}: {
  factors: MFAFactorItem[];
  sessions: SessionItem[];
  identities: IdentityItem[];
}) {
  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Authentication</CardTitle>
        <span className="text-2xs text-muted">
          {factors.filter((f) => f.status === "ACTIVE").length} factor·{factors.length === 1 ? "" : "s"} · {sessions.length} session{sessions.length === 1 ? "" : "s"}
        </span>
      </CardHeader>
      <CardBody className="space-y-5 pt-5">
        <Section title="MFA factors">
          {factors.length === 0 ? (
            <EmptyHint>No MFA factors enrolled — account relies on password only.</EmptyHint>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {factors.map((f) => (
                <li
                  key={f.factor_id}
                  className="flex items-center gap-3 rounded-lg border border-line-1 bg-bg-2 px-3 py-2.5"
                >
                  <FactorIcon kind={f.factor_kind} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-xs font-medium text-fg">{f.friendly_name || f.factor_kind}</span>
                      <Badge tone={f.status === "ACTIVE" ? "success" : f.status === "PENDING_VERIFICATION" ? "warning" : "muted"}>
                        {statusLabel(f.status)}
                      </Badge>
                    </div>
                    <div className="mt-0.5 text-2xs text-muted">
                      {f.factor_kind}
                      {f.last_used_at ? ` · used ${fmtRelative(f.last_used_at)}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Linked identities">
          {identities.length === 0 ? (
            <EmptyHint>No external providers linked.</EmptyHint>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {identities.map((i) => (
                <li
                  key={i.identity_id}
                  className="flex items-center gap-3 rounded-lg border border-line-1 bg-bg-2 px-3 py-2.5"
                >
                  <ProviderIcon kind={i.provider_kind} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-fg">{i.provider_kind}</span>
                      {i.is_primary && <Badge tone="accent">Primary</Badge>}
                      {i.email_verified && <Badge tone="success">Verified</Badge>}
                    </div>
                    <div className="mt-0.5 truncate text-2xs text-muted">{i.email || `linked ${fmtRelative(i.linked_at)}`}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Active sessions">
          {sessions.length === 0 ? (
            <EmptyHint>No active sessions.</EmptyHint>
          ) : (
            <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-line-1">
              {sessions.slice(0, 5).map((s) => (
                <li key={s.session_id} className="flex items-center gap-3 px-3 py-2.5">
                  <DeviceIcon ua={s.user_agent} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-xs font-medium text-fg">{s.device_label || "Unknown device"}</span>
                      {s.is_current && <Badge tone="accent">This session</Badge>}
                      {s.mfa_satisfied && <Badge tone="success">MFA ✓</Badge>}
                    </div>
                    <div className="mt-0.5 truncate text-2xs text-muted">
                      {[s.ip_address, [s.city, s.country].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-2xs text-muted">
                    <div>seen {fmtRelative(s.last_seen_at || s.issued_at)}</div>
                    {s.expires_at && <div>exp {fmtRelative(s.expires_at)}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </CardBody>
    </Card>
  );
}

function FactorIcon({ kind }: { kind: string }) {
  const map: Record<string, string> = {
    TOTP: "🔐",
    EMAIL_OTP: "✉︎",
    WEBAUTHN: "🔑",
    PASSKEY: "🔑",
    PASSPHRASE: "⌨︎",
    BACKUP_CODE: "🛟",
  };
  return (
    <span
      aria-hidden="true"
      className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-line-1 bg-bg-3 text-sm"
    >
      {map[kind] ?? "🔒"}
    </span>
  );
}

function ProviderIcon({ kind }: { kind: string }) {
  const map: Record<string, string> = {
    EMAIL: "✉︎",
    GOOGLE: "G",
    GITHUB: "GH",
    APPLE: "",
  };
  return (
    <span
      aria-hidden="true"
      className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-line-1 bg-bg-3 text-2xs font-semibold text-t-2"
    >
      {map[kind] ?? kind.slice(0, 2)}
    </span>
  );
}

function DeviceIcon({ ua }: { ua?: string }) {
  const isMobile = /Android|iPhone|iPad/i.test(ua ?? "");
  return (
    <span
      aria-hidden="true"
      className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-line-1 bg-bg-3 text-base"
    >
      {isMobile ? "📱" : "💻"}
    </span>
  );
}

// --- activity ---------------------------------------------------------

// Each section shows at most this many rows; the full history lives on
// the dedicated positions / orders pages.
const ROW_CAP = 8;

function ActivityCard({
  positions,
  orders,
  trades,
  available,
  refreshing,
}: {
  positions: PositionItem[];
  orders: OrderItem[];
  trades: SettlementTradeItem[];
  available: { positions: boolean; orders: boolean; trades: boolean };
  refreshing: boolean;
}) {
  const rows = {
    positions: positions.slice(0, ROW_CAP),
    orders: orders.slice(0, ROW_CAP),
    trades: trades.slice(0, ROW_CAP),
  };

  // Resolve names only for the market ids actually on screen. Positions
  // and orders may already carry market_name from an enrichment pass
  // upstream, but the event link only exists on the market detail, so
  // every visible market is looked up regardless — capped inside the
  // hook, and shared with any other page that has already fetched it.
  //
  // Inert without CATALOG_VIEW_ALL: that viewer's catalog ids have been
  // replaced with opaque digests upstream, so every lookup would 404.
  const perms = useMyPerms();
  const resolveCatalog = perms.loaded && perms.has(Perm.CatalogViewAll);
  const marketIDs = useMemo(
    () => [
      ...positions.slice(0, ROW_CAP).map((p) => p.market_id),
      ...orders.slice(0, ROW_CAP).map((o) => o.market_id),
      ...trades.slice(0, ROW_CAP).map((t) => t.market_id),
    ],
    [positions, orders, trades],
  );
  const labels = useMarketLabels(marketIDs, resolveCatalog);

  const counts = [
    available.positions ? `${positions.length} position${positions.length === 1 ? "" : "s"}` : null,
    available.orders ? `${orders.length} open` : null,
    available.trades ? `${trades.length} fill${trades.length === 1 ? "" : "s"}` : null,
  ].filter(Boolean);

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Trading activity</CardTitle>
        <span className="text-2xs text-muted">
          {refreshing ? "Refreshing…" : counts.length > 0 ? counts.join(" · ") : "unavailable"}
        </span>
      </CardHeader>
      <CardBody className="space-y-5 pt-5">
        <Section title="Open positions">
          <ActivityBody
            available={available.positions}
            unavailable="Open positions couldn't be loaded — settlement-service didn't answer. The other sections are unaffected."
            isEmpty={positions.length === 0}
            empty="No open positions."
            minWidth="min-w-[520px]"
          >
            <thead className="border-b border-border bg-surface-2 text-muted">
              <tr>
                <Th>Market</Th>
                <Th>Direction</Th>
                <Th align="right">Quantity</Th>
                <Th align="right">Avg price</Th>
              </tr>
            </thead>
            <tbody>
              {rows.positions.map((p) => (
                <tr key={p.position_id} className="border-b border-border/60 last:border-0">
                  <MarketCell id={p.market_id} name={p.market_name} labels={labels} />
                  <td className="px-3 py-2">
                    <Badge tone={p.direction === "LONG" ? "success" : "danger"}>{p.direction}</Badge>
                  </td>
                  <td className="num px-3 py-2 text-right">{fmtQty(p.quantity)}</td>
                  <td className="num px-3 py-2 text-right">{fmtPrice(p.avg_price)}</td>
                </tr>
              ))}
            </tbody>
          </ActivityBody>
        </Section>

        <Section title="Open orders">
          <ActivityBody
            available={available.orders}
            unavailable="Open orders couldn't be loaded — order-service didn't answer. The other sections are unaffected."
            isEmpty={orders.length === 0}
            empty="No open orders. Filled and cancelled orders are not shown here."
            minWidth="min-w-[600px]"
          >
            <thead className="border-b border-border bg-surface-2 text-muted">
              <tr>
                <Th>Market</Th>
                <Th>Side</Th>
                <Th align="right">Price</Th>
                <Th align="right">Quantity</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {rows.orders.map((o) => (
                <tr key={o.order_id} className="border-b border-border/60 last:border-0">
                  <MarketCell id={o.market_id} name={o.market_name} labels={labels} />
                  <td className="px-3 py-2">
                    <Badge tone={o.side === "BUY" || o.side === "buy" ? "success" : "danger"}>{o.side}</Badge>
                  </td>
                  <td className="num px-3 py-2 text-right">{fmtPrice(o.price)}</td>
                  <td className="num px-3 py-2 text-right">{fmtQty(o.quantity)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-muted">{statusLabel(o.status)}</td>
                </tr>
              ))}
            </tbody>
          </ActivityBody>
        </Section>

        <Section title="Recent trades">
          <ActivityBody
            available={available.trades}
            unavailable="Recent trades couldn't be loaded — settlement-service didn't answer. The other sections are unaffected."
            isEmpty={trades.length === 0}
            empty="No fills yet — this user has never had an order match."
            minWidth="min-w-[820px]"
          >
            <thead className="border-b border-border bg-surface-2 text-muted">
              <tr>
                <Th>Market</Th>
                <Th>Side</Th>
                <Th>Role</Th>
                <Th align="right">Price</Th>
                <Th align="right">Quantity</Th>
                <Th align="right">Fee</Th>
                <Th>Ledger</Th>
                <Th align="right">When</Th>
              </tr>
            </thead>
            <tbody>
              {rows.trades.map((t) => (
                <tr key={t.trade_id} className="border-b border-border/60 last:border-0">
                  <MarketCell id={t.market_id} labels={labels} />
                  <td className="px-3 py-2">
                    <Badge tone={t.side === "BUY" || t.side === "buy" ? "success" : "danger"}>{t.side}</Badge>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-muted">{t.is_maker ? "MAKER" : "TAKER"}</td>
                  <td className="num px-3 py-2 text-right">{fmtPrice(t.price)}</td>
                  <td className="num px-3 py-2 text-right">{fmtQty(t.quantity)}</td>
                  {/* The fill's own leg (SM-549) — this card is the same
                      trade data as Trade History and must not
                      disagree with it. */}
                  <td className="num px-3 py-2 text-right">
                    <CurrencyAmount currency={t.currency} value={t.fee_amount} />
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={ledgerTone(t.ledger_status)}>{statusLabel(t.ledger_status)}</Badge>
                  </td>
                  <td
                    className="whitespace-nowrap px-3 py-2 text-right text-muted"
                    title={fmtDate(t.occurred_at)}
                  >
                    {fmtRelative(t.occurred_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </ActivityBody>
        </Section>
      </CardBody>
    </Card>
  );
}

/**
 * The three states one activity section can be in. `available` is false
 * only when the section's upstream failed — distinct from an empty
 * result, which is a fact about the user rather than about the platform.
 *
 * The table itself is always wider than its column content needs and
 * scrolls inside the card: squeezing eight columns into a phone-width
 * panel is what used to clip the market column.
 */
function ActivityBody({
  available,
  unavailable,
  isEmpty,
  empty,
  minWidth,
  children,
}: {
  available: boolean;
  unavailable: string;
  isEmpty: boolean;
  empty: string;
  minWidth: string;
  children: ReactNode;
}) {
  if (!available) return <ErrorHint>{unavailable}</ErrorHint>;
  if (isEmpty) return <EmptyHint>{empty}</EmptyHint>;
  return (
    <div className="overflow-x-auto rounded-lg border border-line-1">
      <table className={`w-full ${minWidth} text-2xs`}>{children}</table>
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={`whitespace-nowrap px-3 py-2 font-semibold ${align === "right" ? "text-right" : "text-left"}`}
    >
      {children}
    </th>
  );
}

/**
 * The market a row belongs to: market name leading, event underneath.
 *
 * The market leads because it is what the row actually is; the event
 * follows as context, since on a multi-market event a market name alone
 * ("Brazil") doesn't say which contest it belongs to. Same ordering the
 * dispute queue uses.
 *
 * The raw id stays one hover (or click, to copy) away — an operator
 * comparing this panel against a log line still needs it, but it should
 * not be what they read first.
 */
function MarketCell({
  id,
  name,
  labels,
}: {
  id: string;
  /** Name the row already carried, if any; the lookup only fills gaps. */
  name?: string;
  labels: Map<string, MarketLabel>;
}) {
  const label = labels.get(id);
  const market = name || label?.name || "";
  const event = label?.eventName ?? "";
  return (
    <td className="px-3 py-2">
      {/* The cap lives on a block box inside the cell, not on the <td>:
          table auto-layout treats max-width on a cell as a hint it may
          overrule, and then `truncate` has no definite width to work
          against and a long name sets the column width. */}
      <TechHint tech={id} className="block w-full max-w-[240px]">
        <span
          className={`block truncate ${market ? "font-medium text-fg" : "font-mono text-t-3"}`}
          title={market || id}
        >
          {market || shortId(id)}
        </span>
        {event && (
          <span className="mt-0.5 block truncate text-muted" title={event}>
            in {event}
          </span>
        )}
      </TechHint>
    </td>
  );
}

function ledgerTone(s: string): "success" | "warning" | "danger" | "muted" {
  switch (s) {
    case "COMMITTED":
      return "success";
    case "PENDING":
      return "warning";
    case "FAILED":
      return "danger";
    default:
      return "muted";
  }
}

// --- profile sidebar ---------------------------------------------------

function ProfileCard({ profile }: { profile?: UserProfile }) {
  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Profile</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3 pt-5">
        <Field k="Status" v={<Badge tone={statusTone(profile?.status)}>{statusLabel(profile?.status)}</Badge>} />
        <Field k="Display name" v={profile?.display_name || "—"} />
        <Field k="Email" v={profile?.email || "—"} />
        <Field k="KYC" v={profile?.kyc_level || "NONE"} />
        <Field k="Roles" v={(profile?.roles ?? []).join(", ") || "—"} />
        <Field k="Permissions" v={(profile?.permissions ?? []).length.toString()} />
        <Field k="Locale" v={profile?.locale || "—"} />
        <Field k="Timezone" v={profile?.timezone || "—"} />
        <Field k="Last login" v={fmtRelative(profile?.last_login_at)} />
        <Field k="Created" v={fmtDate(profile?.created_at)} />
      </CardBody>
    </Card>
  );
}

function Field({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-muted">{k}</span>
      <span className="text-right text-fg">{v}</span>
    </div>
  );
}

// --- audit timeline ----------------------------------------------------

function AuditCard({ events }: { events: UserAuthEvent[] }) {
  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Recent activity</CardTitle>
        <span className="text-2xs text-muted">{events.length}</span>
      </CardHeader>
      <CardBody className="pt-5">
        {events.length === 0 ? (
          <EmptyHint>
            No auth events recorded. Note: "Last login" above reflects session activity, not entries
            in this timeline.
          </EmptyHint>
        ) : (
          <ol className="relative ml-2 space-y-3 border-l border-line-1 pl-5">
            {events.slice(0, 12).map((e) => (
              <li key={e.event_id} className="relative">
                <span
                  aria-hidden="true"
                  className={`absolute -left-[27px] top-1 grid h-3 w-3 place-items-center rounded-full border-2 border-bg-1 ${severityDot(e.event_severity)}`}
                />
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-fg">{e.event_type}</span>
                      <Badge tone={severityTone(e.event_severity)}>{e.event_severity}</Badge>
                    </div>
                    {e.reason_message && (
                      <div className="mt-0.5 text-2xs text-muted">{e.reason_message}</div>
                    )}
                    {(e.ip_address || e.city || e.country) && (
                      <div className="mt-0.5 text-2xs text-muted">
                        {[e.ip_address, [e.city, e.country].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  <span className="shrink-0 text-2xs text-muted">{fmtRelative(e.occurred_at)}</span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  );
}

function severityTone(s: string): "success" | "warning" | "danger" | "muted" {
  switch (s) {
    case "CRITICAL":
    case "ERROR":
      return "danger";
    case "WARN":
      return "warning";
    case "INFO":
      return "success";
    default:
      return "muted";
  }
}

function severityDot(s: string): string {
  switch (s) {
    case "CRITICAL":
    case "ERROR":
      return "bg-no";
    case "WARN":
      return "bg-warn";
    case "INFO":
      return "bg-yes";
    default:
      return "bg-bg-4";
  }
}

// --- shared bits -------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-2xs font-semibold uppercase tracking-wide text-muted">{title}</div>
      {children}
    </div>
  );
}

// Mirrors the loaded layout — header, KPI row, two columns — so the page
// doesn't jump when the data lands.
function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-28" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Skeleton className="h-56" />
          <Skeleton className="h-72" />
        </div>
        <div className="space-y-6">
          <Skeleton className="h-64" />
        </div>
      </div>
    </div>
  );
}
